import { useState, useEffect } from 'react';

interface StreamCardProps {
  stream: {
    id: string;
    title: string;
    description: string | null;
    thumbnailUrl: string | null;
    priceCents: number;
    isLive: boolean;
    scheduledAt: string | null;
    viewerCount: number;
  };
}

export default function StreamCard({ stream }: StreamCardProps) {
  const [thumbnailCacheBuster, setThumbnailCacheBuster] = useState(Date.now());

  // Periodically refresh thumbnail cache buster for live streams
  useEffect(() => {
    if (!stream.isLive) return;

    const interval = setInterval(() => {
      setThumbnailCacheBuster(Date.now());
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [stream.isLive]);

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    // Format date and time in visitor's local timezone
    const dateTimeStr = date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    // Get timezone abbreviation (e.g., PST, EST, MST)
    const timeZone = date.toLocaleTimeString('en-US', {
      timeZoneName: 'short'
    }).split(' ').pop();
    return `${dateTimeStr} ${timeZone}`;
  };

  // Get the thumbnail URL with cache busting for live streams
  const getThumbnailUrl = () => {
    if (!stream.thumbnailUrl) return null;

    // Add cache buster for live streams to refresh thumbnail periodically
    if (stream.isLive && stream.thumbnailUrl.startsWith('/thumbnails/')) {
      return `${stream.thumbnailUrl}?t=${thumbnailCacheBuster}`;
    }

    return stream.thumbnailUrl;
  };

  const thumbnailUrl = getThumbnailUrl();

  return (
    <div className="card stream-card">
      <div className="stream-card-image">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={stream.title} />
        ) : (
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.5">
            <polygon points="23 7 16 12 23 17 23 7"></polygon>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
        )}
        {stream.isLive && (
          <span className="stream-card-badge badge badge-live">
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'white', display: 'inline-block' }}></span>
            LIVE
          </span>
        )}
        {!stream.isLive && stream.scheduledAt && (
          <span className="stream-card-badge badge badge-scheduled">
            {formatDate(stream.scheduledAt)}
          </span>
        )}
      </div>
      <div className="stream-card-content">
        <h3 className="stream-card-title">{stream.title}</h3>
        {stream.description && (
          <p className="stream-card-description">{stream.description}</p>
        )}
        <div className="stream-card-footer">
          <span className="price price-small">{formatPrice(stream.priceCents)}</span>
          {stream.isLive && stream.viewerCount > 0 && (
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {stream.viewerCount} watching
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
